/**
 * Clickable navigation pill rendered beneath assistant chat answers.
 *
 * Replaces v1's auto-applied set_filters behaviour. The chatbot suggests
 * targeted views via these pills; the user clicks one to navigate to the
 * filtered dashboard view (or directly to a detail page for entity-type
 * suggestions). Nothing happens automatically.
 *
 * Three variants:
 *   - type='list'   → builds /<page>?key=value&… via chatDirectives
 *   - type='entity' → uses the suggestion's explicit `url` (e.g. /members/4100)
 *   - type='stats'  → same path-build as 'list', different icon to hint analytics
 *
 * Tooltip on hover surfaces the exact filter keys/values being applied so the
 * user knows what they're about to navigate into.
 */
import { useNavigate } from 'react-router-dom';
import { ListFilter, ArrowUpRight, BarChart3 } from 'lucide-react';
import type { FilterSuggestion } from '../lib/innoq';
import { buildDirectiveUrl } from '../lib/chatDirectives';
import { Tooltip } from './ui/Tooltip';

interface FilterSuggestionPillProps {
  suggestion: FilterSuggestion;
  /** Highlighted as the pill the user most recently followed, so they can see
   *  which suggestion opened the current dashboard view. */
  active?: boolean;
  /** Called when the pill successfully triggers navigation — lets the chat
   *  widget collapse and scroll this pill into view. Not called when a click is
   *  rejected (non-allowlisted / unbuildable url). */
  onActivate?: () => void;
}

// Whitelist of routes the model is allowed to navigate to via a `type:'entity'`
// suggestion. The model receives untrusted user query text as input — without
// this guard a prompt-injected query could coax the model to emit
// `{type:'entity', url:'javascript:...'}` or `'//attacker.tld/x'` and the pill
// click would follow it (Audit H1). Each entry is `/<page>/<id>` plus an
// optional query string. Any other shape is rejected and the click is no-op.
const ENTITY_URL_RE = /^\/(?:members|bills|votes|debates|parties|sessions|committees)\/[\w.\-]+(?:\?[\w=&%.,\-]*)?$/;

const TYPE_ICON = {
  list:   ListFilter,
  entity: ArrowUpRight,
  stats:  BarChart3,
} as const;

function tooltipFor(s: FilterSuggestion): string {
  if (s.type === 'entity' && s.url) return s.url;
  const filters = s.filters ?? {};
  const entries = Object.entries(filters)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k}=${Array.isArray(v) ? v.join(',') : String(v)}`);
  if (entries.length === 0) return `/${s.page}`;
  return `/${s.page}?${entries.join('&')}`;
}

export function FilterSuggestionPill({ suggestion, active = false, onActivate }: FilterSuggestionPillProps) {
  const navigate = useNavigate();
  const type     = suggestion.type ?? 'list';
  const Icon     = TYPE_ICON[type] ?? ListFilter;

  const handleClick = () => {
    if (type === 'entity' && suggestion.url) {
      // Allowlist guard against prompt-injected URLs (Audit H1).
      if (!ENTITY_URL_RE.test(suggestion.url)) {
        console.warn('[FilterSuggestionPill] rejected non-allowlisted entity url:', suggestion.url);
        return;
      }
      onActivate?.();
      navigate(suggestion.url);
      return;
    }
    const url = buildDirectiveUrl({
      page:    suggestion.page,
      filters: suggestion.filters ?? {},
    });
    if (url) {
      onActivate?.();
      navigate(url);
    }
  };

  return (
    <Tooltip content={<span className="break-all">{tooltipFor(suggestion)}</span>} width={260} className="relative inline-flex">
      <button
        type="button"
        onClick={handleClick}
        aria-pressed={active}
        className={`inline-flex items-center gap-1.5 text-[11.5px] leading-tight text-left px-2.5 py-1.5 rounded-full border transition-colors ${
          active
            ? 'border-[var(--info)] bg-[color-mix(in_srgb,var(--info)_18%,var(--surface))] text-[var(--text-1)] font-medium ring-1 ring-[var(--info)]'
            : 'border-[color-mix(in_srgb,var(--info)_28%,var(--border))] bg-[color-mix(in_srgb,var(--info)_8%,var(--surface))] text-[var(--text-2)] hover:border-[var(--info)] hover:text-[var(--text-1)] hover:bg-[color-mix(in_srgb,var(--info)_14%,var(--surface))]'
        }`}
      >
        <Icon size={12} className="shrink-0 opacity-70" />
        <span className="truncate max-w-[260px]">{suggestion.label}</span>
      </button>
    </Tooltip>
  );
}
